import { Clock, Shirt, ParkingCircle, BookOpen, Baby, HandCoins, MicOff, AlarmClock } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import SectionLabel from '@/components/ui/SectionLabel'
import WhatsAppIcon from '@/components/icons/WhatsAppIcon'
import { WHATSAPP_URL, MAPS_URL } from '@/data/contact'
import { HERO_IMAGE_URL, JOIE_IMAGES } from '@/data/media'
import { PREMIERE_VISITE_FAQ } from '@/data/premiereVisiteFaq'
import { CULTES } from '@/data/cultes'

// Une icône par question, dans le même ordre que data/premiereVisiteFaq.ts
const ICONS: LucideIcon[] = [Clock, Shirt, ParkingCircle, BookOpen, Baby, HandCoins, MicOff, AlarmClock]

const CULTE_DOMINICAL = CULTES.find((c) => c.jour === 'Dimanche')

export default function PremiereVisitePage() {
  return (
    <div>
      <section className="relative text-white px-6 py-16 md:py-24 text-center overflow-hidden">
        {/* Photo de l'assemblée — image Unsplash de PRÉVISUALISATION (voir data/media.ts). */}
        <img src={HERO_IMAGE_URL} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-linear-to-b from-cmp-navy-dark/90 via-cmp-navy/75 to-cmp-navy-dark/95" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 60% 50% at 20% 20%, rgba(201,147,47,0.2) 0%, transparent 100%)',
          }}
        />

        <div className="relative z-10 max-w-xl mx-auto">

          <p className="text-cmp-gold-light text-xs font-bold uppercase tracking-[0.2em] mt-6 mb-3">
            Pour les nouveaux
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight tracking-tight text-balance">
            On a hâte de vous accueillir
          </h1>
          <p className="text-white/80 text-base md:text-lg leading-relaxed mt-4 max-w-md mx-auto">
            Une première fois quelque part soulève toujours des questions. Voici des réponses
            franches à celles qu'on nous pose le plus souvent — pour que vous arriviez serein.
          </p>
        </div>
      </section>

      {/* Qui nous sommes, en images — photos Unsplash de PRÉVISUALISATION (voir data/media.ts).
          TODO: remplacer par de vraies photos de l'assemblée (louange, enfants, accueil, équipe). */}
      <section className="px-4 py-14 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto text-center mb-8">
          <SectionLabel>Qui nous sommes</SectionLabel>
          <h2 className="text-2xl md:text-3xl font-bold text-cmp-navy mt-2">Une famille qui vous ressemble</h2>
          <p className="text-gray-600 leading-relaxed mt-3 max-w-md mx-auto">
            La louange, les rires entre voisins de banc, les enfants qui courent après le culte — voilà
            à quoi ressemble un dimanche chez nous.
          </p>
        </div>

        <div className="max-w-3xl mx-auto grid grid-cols-2 gap-3 md:gap-4">
          {JOIE_IMAGES.map((img, i) => (
            <div key={img.src} className={`overflow-hidden rounded-2xl ${i === 0 ? 'rounded-tl-4xl' : ''}`}>
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full aspect-square object-cover transition-transform duration-300 hover:scale-105"
              />
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 py-14 md:py-20 bg-cmp-cream">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <SectionLabel>Vos questions</SectionLabel>
            <h2 className="text-2xl md:text-3xl font-bold text-cmp-navy mt-2">On répond franchement à tout</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {PREMIERE_VISITE_FAQ.map(({ question, reponse }, i) => {
              const Icon = ICONS[i]
              return (
                <div
                  key={question}
                  className="bg-white rounded-2xl rounded-tl-3xl p-6 shadow-[0_2px_10px_rgba(15,31,92,0.06)] ring-1 ring-cmp-navy/6 transition-shadow hover:shadow-[0_8px_24px_rgba(15,31,92,0.1)]"
                >
                  <div className="w-11 h-11 rounded-full bg-cmp-sky-light flex items-center justify-center mb-4 ring-4 ring-cmp-gold-light/15">
                    <Icon size={19} className="text-cmp-blue" />
                  </div>
                  <h3 className="font-bold text-cmp-navy text-base leading-snug">{question}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mt-2">{reponse}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <section className="px-4 pt-10 md:p-20">
        <div className="max-w-2xl mx-auto bg-linear-to-br from-cmp-navy to-cmp-navy-dark rounded-3xl rounded-tl-4xl p-8 md:p-10 text-center text-white shadow-[0_16px_40px_rgba(15,31,92,0.18)]">
          <p className="text-cmp-gold-light text-xs font-bold uppercase tracking-widest">Vous avez vos réponses</p>
          <h2 className="text-2xl md:text-3xl font-bold mt-3">Alors, on vous attend ce dimanche ?</h2>
          <p className="text-white/75 text-sm leading-relaxed mt-2 max-w-sm mx-auto">
            Un petit message avant de venir, et quelqu'un sera là pour vous accueillir
            personnellement à votre arrivée — sans engagement, juste pour dire bonjour.
          </p>

          {CULTE_DOMINICAL && (
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mt-5 text-sm font-semibold">
              <Clock size={14} className="text-cmp-gold-light" />
              {CULTE_DOMINICAL.jour} · {CULTE_DOMINICAL.creneaux.map((c) => c.heureDebut).join(' et ')}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-center">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white text-cmp-navy font-bold rounded-full py-3.5 px-7 text-sm shadow-lg hover:bg-cmp-sky-light transition-colors active:scale-95"
            >
              <WhatsAppIcon size={17} />
              Prévenez-nous sur WhatsApp
            </a>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-semibold rounded-full py-3.5 px-7 text-sm hover:bg-white/10 transition-colors active:scale-95"
            >
              Voir l'itinéraire
            </a>
          </div>

          <p className="text-white/50 text-xs mt-5">
            On vous répond rapidement — et personne ne vous mettra sur le devant de la scène, promis.
          </p>
        </div>
      </section>
    </div>
  )
}
